'use client';

import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { type GridColDef } from '@andritzot/metris-web-ui/mui-x/data-grid';
import AddOutlined from '@mui/icons-material/AddOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { Column } from 'primereact/column';
import { confirmPopup } from 'primereact/confirmpopup';
import { type DataTableProps, DataTableValueArray } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import { type MouseEvent, type ReactNode, useState } from 'react';

import AddItemDialog from '../AddItemDialog/AddItemDialog';
import CustomDataTable from '../CustomDataTable/CustomDataTable';

type CrudTableProps<T> = DataTableProps<DataTableValueArray> & {
  currentValues: T[];
  totalValues: T[];
  children: ReactNode;
  isLoading: boolean;
  dataKey: keyof T & string;
  addColumns: GridColDef[];
  onAddAsync: ((values: T[]) => Promise<void> | void) | null;
  onDeleteAsync: ((id: T) => Promise<void> | void) | null;
};

const CrudTable = <T extends object>({
  currentValues,
  totalValues,
  children,
  isLoading,
  onAddAsync,
  onDeleteAsync,
  dataKey,
  addColumns,
  ...rest
}: CrudTableProps<T>) => {
  const [addDialogVisible, setAddDialogVisible] = useState(false);

  const removeElement = (e: MouseEvent<HTMLButtonElement>, element: T) => {
    confirmPopup({
      target: e.currentTarget,
      message: 'Remove this item?',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',

      accept: async () => {
        await onDeleteAsync?.(element);
      },
    });
  };

  const leftToolbarTemplate = () => (
    <Button
      startIcon={<AddOutlined />}
      variant="outlined"
      color="success"
      sx={{ mr: 1, mb: 1 }}
      aria-haspopup
      aria-controls="overlay_panel"
      onClick={() => {
        setAddDialogVisible(true);
      }}
    >
      Add
    </Button>
  );

  const actionBodyTemplate = (rowData: T) => (
    <div className="actions">
      <Tooltip title="Delete">
        <IconButton
          variant="outlined"
          color="error"
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            removeElement(e, rowData);
          }}
        >
          <DeleteOutlined />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <>
      {onAddAsync && <Toolbar className="p-mb-4 p-toolbar" start={leftToolbarTemplate} />}
      <CustomDataTable {...rest} value={currentValues} dataKey={dataKey} rows={10}>
        {onDeleteAsync && <Column headerStyle={{ width: '10%', minWidth: '8rem' }} body={actionBodyTemplate} />}
        {children}
      </CustomDataTable>
      {onAddAsync && (
        <AddItemDialog
          values={totalValues}
          isLoading={isLoading}
          visible={addDialogVisible}
          setVisible={setAddDialogVisible}
          getRowId={(row) => (row as Record<string, unknown>)[dataKey] as string | number}
          columns={addColumns}
          onAddAsync={onAddAsync}
        />
      )}
    </>
  );
};

export default CrudTable;
